/**
 * Maps common English country names (and a few aliases) to ISO 3166-1 alpha-2 codes for compact UI.
 * Unknown names: if already 2-letter A–Z, returned uppercased; otherwise returns a short uppercase heuristic.
 */
const NAME_TO_ISO: Record<string, string> = {
  afghanistan: 'AF',
  albania: 'AL',
  algeria: 'DZ',
  andorra: 'AD',
  angola: 'AO',
  argentina: 'AR',
  armenia: 'AM',
  australia: 'AU',
  austria: 'AT',
  azerbaijan: 'AZ',
  bahamas: 'BS',
  bahrain: 'BH',
  bangladesh: 'BD',
  belarus: 'BY',
  belgium: 'BE',
  belize: 'BZ',
  bolivia: 'BO',
  'bosnia and herzegovina': 'BA',
  botswana: 'BW',
  brazil: 'BR',
  brunei: 'BN',
  bulgaria: 'BG',
  cambodia: 'KH',
  cameroon: 'CM',
  canada: 'CA',
  chile: 'CL',
  china: 'CN',
  colombia: 'CO',
  'costa rica': 'CR',
  croatia: 'HR',
  cuba: 'CU',
  cyprus: 'CY',
  'czech republic': 'CZ',
  czechia: 'CZ',
  denmark: 'DK',
  'dominican republic': 'DO',
  ecuador: 'EC',
  egypt: 'EG',
  'el salvador': 'SV',
  estonia: 'EE',
  ethiopia: 'ET',
  finland: 'FI',
  france: 'FR',
  georgia: 'GE',
  germany: 'DE',
  ghana: 'GH',
  greece: 'GR',
  guatemala: 'GT',
  honduras: 'HN',
  'hong kong': 'HK',
  hungary: 'HU',
  iceland: 'IS',
  india: 'IN',
  indonesia: 'ID',
  iran: 'IR',
  iraq: 'IQ',
  ireland: 'IE',
  israel: 'IL',
  italy: 'IT',
  jamaica: 'JM',
  japan: 'JP',
  jordan: 'JO',
  kazakhstan: 'KZ',
  kenya: 'KE',
  kosovo: 'XK',
  kuwait: 'KW',
  latvia: 'LV',
  lebanon: 'LB',
  libya: 'LY',
  liechtenstein: 'LI',
  lithuania: 'LT',
  luxembourg: 'LU',
  malaysia: 'MY',
  malta: 'MT',
  mexico: 'MX',
  moldova: 'MD',
  monaco: 'MC',
  montenegro: 'ME',
  morocco: 'MA',
  nepal: 'NP',
  netherlands: 'NL',
  'the netherlands': 'NL',
  holland: 'NL',
  'new zealand': 'NZ',
  nicaragua: 'NI',
  nigeria: 'NG',
  'north macedonia': 'MK',
  macedonia: 'MK',
  norway: 'NO',
  oman: 'OM',
  pakistan: 'PK',
  panama: 'PA',
  paraguay: 'PY',
  peru: 'PE',
  philippines: 'PH',
  poland: 'PL',
  portugal: 'PT',
  qatar: 'QA',
  romania: 'RO',
  russia: 'RU',
  'russian federation': 'RU',
  'saudi arabia': 'SA',
  serbia: 'RS',
  singapore: 'SG',
  slovakia: 'SK',
  slovenia: 'SI',
  'south africa': 'ZA',
  'south korea': 'KR',
  korea: 'KR',
  spain: 'ES',
  sweden: 'SE',
  switzerland: 'CH',
  taiwan: 'TW',
  thailand: 'TH',
  tunisia: 'TN',
  turkey: 'TR',
  turkmenistan: 'TM',
  ukraine: 'UA',
  'united arab emirates': 'AE',
  uae: 'AE',
  'united kingdom': 'GB',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  'united states': 'US',
  'united states of america': 'US',
  usa: 'US',
  uruguay: 'UY',
  uzbekistan: 'UZ',
  venezuela: 'VE',
  vietnam: 'VN',
  'viet nam': 'VN',
};

function singleNameToIso(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (/^[A-Za-z]{2}$/.test(t)) return t.toUpperCase();
  const k = t.toLowerCase();
  return NAME_TO_ISO[k] ?? t.toUpperCase().slice(0, 2);
}

/** Comma-separated country field → comma-separated ISO codes (e.g. "United States" → "US"). */
export function formatCountryFieldAsIso(countryField: string): string {
  return countryField
    .split(',')
    .map((p) => singleNameToIso(p))
    .filter(Boolean)
    .join(', ');
}
